import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../utils/auth';
import { getJson, integrationError, money, percent, postJson } from '../helpers';
import { reportPath } from '../reports';

function formatAuditValue(col) {
  const value = col.report_value;
  if (value && typeof value === 'object') return JSON.stringify(value);
  if (col.column_key === 'percent_complete' || col.column_key === 'profit_margin_percent') {
    return percent(value);
  }
  if (typeof value === 'number') return money(value);
  return String(value ?? '—');
}

function AuditSourceTable({ records }) {
  if (!records || records.length === 0) {
    return <p className="pc-muted">No line-level records.</p>;
  }
  const keys = Object.keys(records[0]).filter((key) => {
    const lower = key.toLowerCase();
    return lower !== 'vuid' && !lower.endsWith('_vuid');
  });
  return (
    <div className="pc-table-scroll">
      <table className="pc-table">
        <thead>
          <tr>
            {keys.map((key) => (
              <th key={key}>{key.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr key={index}>
              {keys.map((key) => (
                <td key={key}>
                  {typeof record[key] === 'object' && record[key] !== null
                    ? JSON.stringify(record[key])
                    : record[key] == null
                      ? '—'
                      : String(record[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function WipReportActions({
  selectedPeriod,
  period,
  rows,
  projectVuid,
  eacEnabled,
  onRefresh,
}) {
  const navigate = useNavigate();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const periodOpen = period?.status === 'open';
  const postedBy = getUser()?.username || 'User';

  const requirePeriod = () => {
    if (selectedPeriod) return true;
    setError('Select an accounting period first.');
    return false;
  };

  const handlePreviewJournalEntries = async () => {
    if (!requirePeriod()) return;
    setError('');
    setMessage('');
    setPreviewLoading(true);
    try {
      const data = await getJson(`/api/journal-entries/preview-from-posted/${selectedPeriod}`);
      if (!data.success) {
        setError(data.error || 'Unable to preview journal entries.');
        return;
      }
      setPreviewData({
        ...data,
        preview_entries: data.journal_entries || [],
        accounting_period: {
          month: data.accounting_period?.month || period?.month,
          year: data.accounting_period?.year || period?.year,
        },
      });
    } catch (err) {
      setError(integrationError(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePostAllOverUnderBilling = async () => {
    if (!requirePeriod()) return;
    const alreadyPosted = (rows || []).filter(
      (item) =>
        ((item.over_billing || 0) > 0 && item.over_billing_posted) ||
        ((item.under_billing || 0) > 0 && item.under_billing_posted)
    );
    const toPost = (rows || []).filter(
      (item) =>
        ((item.over_billing || 0) > 0 && !item.over_billing_posted) ||
        ((item.under_billing || 0) > 0 && !item.under_billing_posted)
    );
    if (toPost.length === 0) {
      setError(
        alreadyPosted.length
          ? `Over/under billing is already posted for: ${alreadyPosted.map((item) => item.project_number).join(', ')}`
          : 'No projects have over/under billing amounts to post.'
      );
      return;
    }
    const totalOver = toPost.reduce((sum, item) => sum + (item.over_billing || 0), 0);
    const totalUnder = toPost.reduce((sum, item) => sum + (item.under_billing || 0), 0);
    if (
      !window.confirm(
        `Post over/under billing for ${toPost.length} project(s)?\n\nTotal Overbilled: ${money(totalOver)}\nTotal Underbilled: ${money(totalUnder)}`
      )
    ) {
      return;
    }
    setPosting(true);
    setError('');
    setMessage('');
    const errors = [];
    let successCount = 0;
    for (const project of toPost) {
      try {
        if ((project.over_billing || 0) > 0 && !project.over_billing_posted) {
          await postJson('/api/wip/post-over-under-billing', {
            project_vuid: project.project_vuid,
            accounting_period_vuid: selectedPeriod,
            over_under_amount: project.over_billing,
            posted_by: postedBy,
          });
          successCount += 1;
        }
        if ((project.under_billing || 0) > 0 && !project.under_billing_posted) {
          await postJson('/api/wip/post-over-under-billing', {
            project_vuid: project.project_vuid,
            accounting_period_vuid: selectedPeriod,
            over_under_amount: -project.under_billing,
            posted_by: postedBy,
          });
          successCount += 1;
        }
      } catch (err) {
        errors.push(`${project.project_number}: ${integrationError(err)}`);
      }
    }
    setPosting(false);
    if (onRefresh) onRefresh();
    if (errors.length) {
      setError(`Posted ${successCount} adjustment(s). ${errors.length} failed:\n${errors.join('\n')}`);
    } else {
      setMessage(`Posted ${successCount} over/under billing adjustment(s).`);
    }
  };

  const handleCloseMonth = async () => {
    if (!requirePeriod()) return;
    setClosing(true);
    setError('');
    setMessage('');
    try {
      const data = await postJson('/api/wip/close-month', {
        accounting_period_vuid: selectedPeriod,
      });
      if (!data.success) {
        setError(data.error || 'Unable to close the month.');
        return;
      }
      setPreviewData(null);
      setMessage(`Month closed. Created ${data.journal_entries_created || 0} journal entries.`);
      if (onRefresh) await onRefresh();
      navigate(`${reportPath(projectVuid, 'journal-entries')}`);
    } catch (err) {
      setError(integrationError(err));
    } finally {
      setClosing(false);
    }
  };

  const handleOpenAuditReport = async () => {
    if (!requirePeriod()) return;
    setMenuOpen(false);
    setAuditOpen(true);
    setAuditLoading(true);
    setAuditData(null);
    setError('');
    try {
      const data = await getJson('/api/wip/audit-report', {
        accounting_period_vuid: selectedPeriod,
      });
      const projectNumber = rows?.[0]?.project_number;
      if (projectVuid && projectNumber && Array.isArray(data.projects)) {
        setAuditData({
          ...data,
          projects: data.projects.filter((project) => project.wip_row?.project_number === projectNumber),
        });
      } else {
        setAuditData(data);
      }
    } catch (err) {
      setError(integrationError(err));
      setAuditOpen(false);
    } finally {
      setAuditLoading(false);
    }
  };

  if (!selectedPeriod) return null;

  const summary = previewData?.preview_summary || {};
  const entries = previewData?.preview_entries || [];

  return (
    <>
      <div className="pc-title-actions">
        <button
          type="button"
          className="pc-btn pc-btn-secondary"
          onClick={handlePreviewJournalEntries}
          disabled={previewLoading}
        >
          {previewLoading ? 'Previewing…' : 'Preview Journal Entries'}
        </button>
        <button
          type="button"
          className="pc-btn pc-btn-secondary"
          onClick={handlePostAllOverUnderBilling}
          disabled={posting || !periodOpen}
        >
          {posting ? 'Posting…' : 'Post All Over/Under Billing'}
        </button>
        <div className="pc-action-menu">
          <button
            type="button"
            className="pc-btn"
            onClick={() => setMenuOpen((open) => !open)}
            disabled={closing}
          >
            {closing ? 'Closing…' : 'Close Month'}
          </button>
          {menuOpen ? (
            <>
              <div className="pc-action-menu-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="pc-action-menu-list">
                <button
                  type="button"
                  disabled={!periodOpen || closing}
                  onClick={() => {
                    setMenuOpen(false);
                    handleCloseMonth();
                  }}
                >
                  Close Month — Generate Journal Entries
                </button>
                <button type="button" onClick={handleOpenAuditReport}>
                  WIP Audit Detail Report (print / PDF)
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
      {message ? <div className="pc-flash pc-flash-ok pc-wip-flash">{message}</div> : null}
      {error ? <div className="pc-flash pc-flash-err pc-wip-flash">{error}</div> : null}

      {previewData
        ? createPortal(
            <div className="pc-modal-backdrop" onClick={() => setPreviewData(null)}>
              <div className="pc-modal pc-modal-wide" onClick={(event) => event.stopPropagation()}>
                <div className="pc-modal-head">
                  <h2>
                    Journal Entries Preview — {previewData.accounting_period?.month}/{previewData.accounting_period?.year}
                  </h2>
                  <button type="button" className="pc-modal-close" onClick={() => setPreviewData(null)} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="pc-modal-body">
                  <div className="pc-kv-row">
                    <span>Total entries</span>
                    <strong>{summary.total_entries || entries.length || 0}</strong>
                  </div>
                  <div className="pc-kv-row">
                    <span>Total debits</span>
                    <strong>{money(summary.total_debits)}</strong>
                  </div>
                  <div className="pc-kv-row">
                    <span>Total credits</span>
                    <strong>{money(summary.total_credits)}</strong>
                  </div>
                  {summary.validation_errors?.length ? (
                    <div className="pc-flash pc-flash-err">
                      {summary.validation_errors.map((item) => (
                        <div key={item}>{item}</div>
                      ))}
                    </div>
                  ) : null}
                  {summary.is_balanced ? (
                    <div className="pc-flash pc-flash-ok">All journal entries are properly balanced.</div>
                  ) : null}
                  <div className="pc-table-scroll" style={{ maxHeight: 360, marginTop: 12 }}>
                    <table className="pc-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Reference</th>
                          <th>Project</th>
                          <th className="pc-right">Debit</th>
                          <th className="pc-right">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, index) => {
                          const lines = entry.line_items || [];
                          const typeDisplay =
                            (entry.type && String(entry.type).trim()) ||
                            (entry.reference_type &&
                              String(entry.reference_type)
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (letter) => letter.toUpperCase())) ||
                            'Unknown';
                          return (
                            <React.Fragment key={index}>
                              <tr>
                                <td rowSpan={Math.max(lines.length, 1)}>{typeDisplay}</td>
                                <td rowSpan={Math.max(lines.length, 1)}>{entry.reference_number || '—'}</td>
                                <td rowSpan={Math.max(lines.length, 1)}>
                                  {entry.project_name || '—'}
                                  {entry.project_number ? <div className="pc-sub">{entry.project_number}</div> : null}
                                </td>
                                <td className="pc-right">{lines[0]?.debit_amount ? money(lines[0].debit_amount) : '—'}</td>
                                <td className="pc-right">{lines[0]?.credit_amount ? money(lines[0].credit_amount) : '—'}</td>
                              </tr>
                              {lines.slice(1).map((line, lineIndex) => (
                                <tr key={`${index}-${lineIndex}`}>
                                  <td className="pc-right">{line.debit_amount ? money(line.debit_amount) : '—'}</td>
                                  <td className="pc-right">{line.credit_amount ? money(line.credit_amount) : '—'}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="pc-modal-actions">
                  <button type="button" className="pc-btn-secondary" onClick={() => setPreviewData(null)}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="pc-btn"
                    onClick={handleCloseMonth}
                    disabled={!periodOpen || closing}
                  >
                    {closing ? 'Closing…' : 'Close Month & Generate Entries'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {auditOpen
        ? createPortal(
            <div className="pc-modal-backdrop" onClick={() => !auditLoading && setAuditOpen(false)}>
              <style>{`
                @media print {
                  body * { visibility: hidden; }
                  .wip-audit-print-root, .wip-audit-print-root * { visibility: visible; }
                  .wip-audit-print-root { position: absolute; left: 0; top: 0; width: 100%; max-height: none !important; overflow: visible !important; }
                  .wip-audit-no-print { display: none !important; }
                }
              `}</style>
              <div className="pc-modal pc-modal-wide wip-audit-print-root" onClick={(event) => event.stopPropagation()}>
                <div className="pc-modal-head wip-audit-no-print">
                  <h2>WIP Audit Detail Report</h2>
                  <div className="pc-title-actions">
                    <button type="button" className="pc-btn pc-btn-secondary" onClick={() => window.print()}>
                      Print / Save PDF
                    </button>
                    <button
                      type="button"
                      className="pc-modal-close"
                      onClick={() => {
                        setAuditOpen(false);
                        setAuditData(null);
                      }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="pc-modal-body">
                  {auditLoading ? (
                    <div className="pc-empty">Loading audit detail…</div>
                  ) : auditData ? (
                    <>
                      <p className="pc-muted">
                        Accounting period:{' '}
                        {auditData.accounting_period
                          ? `${auditData.accounting_period.month}/${auditData.accounting_period.year} (${auditData.accounting_period.status})`
                          : '—'}
                      </p>
                      <p className="pc-muted">Generated: {auditData.generated_at || '—'}</p>
                      {(auditData.projects || []).map((project, index) => {
                        const row = project.wip_row || {};
                        return (
                          <section key={`${row.project_number || 'project'}-${index}`} className="pc-calc-panel">
                            <h3>
                              {row.project_number} — {row.project_name}
                            </h3>
                            <p className="pc-muted">
                              Contract: {row.contract_number || '—'} · Customer: {row.customer_name || '—'}
                            </p>
                            <div className="pc-kv-row">
                              <span>Original Amount</span>
                              <strong>{money(row.original_contract_amount)}</strong>
                            </div>
                            <div className="pc-kv-row">
                              <span>Change Orders</span>
                              <strong>
                                {money(row.change_orders)}
                                {row.includes_forecast_pending_cos ? '*' : ''}
                              </strong>
                            </div>
                            <div className="pc-kv-row">
                              <span>Total Contract</span>
                              <strong>{money(row.total_contract_amount)}</strong>
                            </div>
                            <div className="pc-kv-row">
                              <span>
                                {eacEnabled && row.estimated_at_completion ? 'Est. Cost at Completion' : 'Current Budget'}
                              </span>
                              <strong>
                                {money(
                                  eacEnabled && row.estimated_at_completion
                                    ? row.estimated_at_completion
                                    : row.current_budget_amount
                                )}
                              </strong>
                            </div>
                            <div className="pc-kv-row">
                              <span>Costs to Date</span>
                              <strong>{money(row.costs_to_date)}</strong>
                            </div>
                            <div className="pc-kv-row">
                              <span>Earned Revenue</span>
                              <strong>{money(row.revenue_recognized)}</strong>
                            </div>
                            <div className="pc-kv-row">
                              <span>Overbilled</span>
                              <strong>{money(row.over_billing)}</strong>
                            </div>
                            <div className="pc-kv-row">
                              <span>Underbilled</span>
                              <strong>{money(row.under_billing)}</strong>
                            </div>
                            {(project.column_details || []).map((col) => (
                              <div key={col.column_key} style={{ marginTop: 12 }}>
                                <div className="pc-label">{col.label}</div>
                                <p className="pc-muted">{col.formula}</p>
                                <div className="pc-kv-row">
                                  <span>Report value</span>
                                  <strong>{formatAuditValue(col)}</strong>
                                </div>
                                <AuditSourceTable records={col.source_records} />
                              </div>
                            ))}
                          </section>
                        );
                      })}
                    </>
                  ) : (
                    <div className="pc-empty">No audit data.</div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
